import { ref, computed, type ComputedRef } from 'vue'
import {
  hasLightingEditableField,
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_TARGET,
  type lightingColor,
  type LightingConfigData,
  type LightingEffectDefinition,
  type RGB,
  LightingDomainType,
} from '@rapoo/device-sdk'
import { defaultReportError } from '@/composables/useErrorLogger'
import { useLoading } from '@/composables/useLoading'
import {
  useLightingSDK,
  beginLatest,
  createLatestCommitter,
  useLightingCapability,
  useLightingLifecycle,
} from './useLightingSDK'

/**
 * 侧灯（LIGHTING_TARGET.Side）业务。
 *
 * - 模式：getSideEffect / setSideEffect
 * - 开关：getSideLightDynamicEnable / setSideLightDynamicEnable
 * - 领域固定 Side；不监听亮度/速度上报（上报属按键灯）
 */

export type UseSideLightingOptions = {
  autoInit?: boolean
}

const SIDE_DOMAIN = LightingDomainType.Side

export function useSideLighting(options: UseSideLightingOptions = {}) {
  const { autoInit = true } = options
  const { isLoading, withLoading } = useLoading()
  const { currentDevice, hasSideLighting } = useLightingCapability()

  const {
    getEffects,
    getConfig,
    setConfig,
    getSideEffect,
    setSideEffect,
    getSideLightDynamicEnable,
    setSideLightDynamicEnable,
    getLighting,
    hasSideEffectApi,
    hasSideLightEnableApi,
  } = useLightingSDK()

  const rgb = ref<RGB>({ r: 255, g: 255, b: 255 })
  const isColorful = ref(false)
  const effects = ref<LightingEffectDefinition[]>([])
  const currentMode = ref(-1)
  const brightness = ref(60)
  const speed = ref(60)
  const enabledSideLighting = ref<boolean | null>(null)

  const effectConfRequestId = ref(0)
  const initRequestId = ref(0)

  const currentEffectInfo = computed(() =>
    effects.value.find((e) => e.id === currentMode.value),
  ) as ComputedRef<LightingEffectDefinition | undefined>

  /** 当前灯效的速度调节范围（默认 0-100 连续；GCOME_AP 等档位设备从配置读取）。 */
  const speedRange = computed(
    () => currentEffectInfo.value?.speedRange ?? { min: 0, max: 100, step: 1 },
  )

  /** 当前灯效的亮度调节范围（默认 0-100 连续；GCOME_AP 为 0-100 步进 25 的 5 档）。 */
  const brightnessRange = computed(
    () => currentEffectInfo.value?.brightnessRange ?? { min: 0, max: 100, step: 1 },
  )

  const resetUi = () => {
    effects.value = []
    enabledSideLighting.value = null
    currentMode.value = -1
  }

  const applyConfig = (data: LightingConfigData) => {
    brightness.value = data.brightness ?? 60
    speed.value = data.speed ?? 60
    isColorful.value = data.isColorful ?? false
    rgb.value = data.rgb ?? rgb.value
  }

  const loadEffectConf = async (index: number, stillWanted?: () => boolean) => {
    const latest = beginLatest(effectConfRequestId)
    // 调用方可声明「结果已无人需要」（亮度 / 速度连击中的旧回读），避免旧值闪回滑块
    const task = { alive: () => latest.alive() && (stillWanted?.() ?? true) }
    if (!getLighting() || index < 0) return
    try {
      const data = await getConfig(SIDE_DOMAIN, index)
      if (!task.alive() || !data) return
      applyConfig(data)
    } catch (error) {
      console.error('Failed to load side lighting config:', error)
      defaultReportError(`Failed to load side lighting config: ${JSON.stringify(error)}`)
    }
  }

  const reapplyCurrentMode = async (stillWanted?: () => boolean) => {
    const mode = currentEffectInfo.value
    if (!mode || !hasSideEffectApi()) return
    await setSideEffect(mode.id)
    // 重下发期间已有更新的同字段值在等：这次回读只会把旧值闪回 UI，直接跳过
    if (stillWanted && !stillWanted()) return
    await loadEffectConf(mode.id, stillWanted)
  }

  const init = async (opts?: { force?: boolean }) => {
    if (!currentDevice.value || !getLighting() || !hasSideLighting.value) return

    const task = beginLatest(initRequestId)
    effectConfRequestId.value++

    await withLoading(
      async () => {
        try {
          effects.value = await getEffects(LIGHTING_TARGET.Side)
          if (!task.alive()) return

          if (hasSideLightEnableApi()) {
            enabledSideLighting.value = (await getSideLightDynamicEnable()) ?? false
          } else {
            enabledSideLighting.value = false
          }
          if (!task.alive()) return

          if (hasSideEffectApi()) {
            const effectId = await getSideEffect()
            if (effectId !== undefined) currentMode.value = effectId
          }
          if (!task.alive()) return

          await loadEffectConf(currentMode.value)
        } catch (err) {
          console.error('Failed to initialize side lighting:', err)
          defaultReportError(`Failed to initialize side lighting: ${JSON.stringify(err)}`)
        }
      },
      { skipIfLoading: opts?.force ? false : true },
    )
  }

  const setMode = async (mode?: LightingEffectDefinition) => {
    if (!getLighting() || !mode || !hasSideEffectApi()) return
    currentMode.value = mode.id

    await withLoading(async () => {
      try {
        await setSideEffect(mode.id)
        await loadEffectConf(mode.id)
      } catch (err) {
        console.error('Failed to set side lighting mode:', err)
        defaultReportError(`Failed to set side lighting mode: ${JSON.stringify(err)}`)
      }
    })
  }

  const setColor = async (color: lightingColor) => {
    if (!getLighting()) return
    const info = currentEffectInfo.value
    const canColor = hasLightingEditableField(info, LIGHTING_EDITABLE_FIELD.Color)
    const canCycle = hasLightingEditableField(info, LIGHTING_EDITABLE_FIELD.ColorCycle)
    if (!canColor && !canCycle) return

    if (color === 'linear' && !canCycle) {
      console.warn('Current side effect does not support color cycle lighting')
      return
    }
    isColorful.value = Boolean(canCycle && color === 'linear')

    await withLoading(async () => {
      try {
        if (isColorful.value && canCycle) {
          await setConfig(SIDE_DOMAIN, currentMode.value, { isColorful: true })
        } else if (canColor) {
          await setConfig(SIDE_DOMAIN, currentMode.value, {
            isColorful: false,
            rgb: rgb.value,
          })
        }
      } catch (error) {
        console.error('Failed to set side color:', error)
        defaultReportError(`Failed to set side color: ${JSON.stringify(error)}`)
      } finally {
        await reapplyCurrentMode()
      }
    })
  }

  /** 同字段合并写入：连点只落最后一次；中间轮不重下发 / 回读，避免旧值闪回 UI */
  const commitLatest = createLatestCommitter<string, Partial<LightingConfigData>>(
    async (label, partial, { hasNewer, settle }) => {
      try {
        await setConfig(SIDE_DOMAIN, currentMode.value, partial)
      } catch (err) {
        console.error(`Failed to set side ${label}:`, err)
        defaultReportError(`Failed to set side ${label}: ${JSON.stringify(err)}`)
      } finally {
        // 连击中先静默一小段；期间来了更新值就不重下发 / 回读，交给最后一轮
        if (!(await settle())) await reapplyCurrentMode(() => !hasNewer())
      }
    },
  )

  const commitConfig = async (partial: Partial<LightingConfigData>, label: string) => {
    if (!getLighting()) return
    await commitLatest(label, partial)
  }

  const setBrightness = async (value?: number) => {
    if (value !== undefined) brightness.value = value
    await commitConfig({ brightness: brightness.value }, 'brightness')
  }

  const setSpeed = async (value?: number) => {
    if (value !== undefined) speed.value = value
    await commitConfig({ speed: speed.value }, 'speed')
  }

  const setSideEnabled = async (enable: boolean) => {
    if (!getLighting() || !hasSideLightEnableApi()) return
    await withLoading(async () => {
      try {
        await setSideLightDynamicEnable(enable)
        enabledSideLighting.value = enable
      } catch (error) {
        console.error('Failed to set side lighting enable:', error)
        defaultReportError(`Failed to set side lighting enable: ${JSON.stringify(error)}`)
      }
    })
  }

  useLightingLifecycle({
    autoInit,
    reset: resetUi,
    init,
    onInvalidate: () => {
      initRequestId.value++
      effectConfRequestId.value++
    },
  })

  return {
    isLoading,
    hasSideLighting,
    enabledSideLighting,
    effects,
    currentMode,
    currentEffectInfo,
    rgb,
    isColorful,
    brightness,
    speed,
    speedRange,
    brightnessRange,
    init,
    setMode,
    setColor,
    setBrightness,
    setSpeed,
    setSideEnabled,
  }
}
